import { formatMoney } from '@theme/money-formatting';

const STORAGE_KEY = 'ibm:quick-order-details';
const FIELDS = ['name', 'phone', 'address', 'city'];

/**
 * Reads the remembered buyer details. Storage can be unavailable (private mode, blocked cookies),
 * so every access is guarded and the form simply works without it.
 * @returns {Record<string, string> | null}
 */
function readDetails() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/** @param {Record<string, string> | null} details */
function writeDetails(details) {
  try {
    if (details) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(details));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Remembering details is a convenience only.
  }
}

/**
 * Normalises Pakistani mobile numbers to 03XXXXXXXXX, which Shopify checkout accepts for PK.
 * Numbers that don't look Pakistani are returned with only spaces/dashes removed.
 * @param {string} value
 */
function normalisePhone(value) {
  const compact = value.replace(/[\s\-()]/g, '');
  const match = compact.match(/^(?:\+?92|0092|0)?(3\d{9})$/);
  return match ? `0${match[1]}` : compact;
}

/**
 * COD quick order: collects the selected items (main product + quantity deal, add-ons, or a bundle),
 * shows a live total, and sends the buyer to a pre-filled checkout via a cart permalink.
 */
class QuickOrderForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.currency = this.dataset.currency || 'PKR';
    this.remember = this.dataset.remember === 'true';

    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.matches('[name="qo-qty"], [data-qo-addon], [data-qo-bundle]')) {
        this.updateTotals();
      }
    });
    this.setUpBulkStepper();
    this.querySelector('[data-qo-forget]')?.addEventListener('click', this.forgetDetails.bind(this));

    if (this.remember) this.restoreDetails();
    this.updateTotals();
    if (this.dataset.sticky === 'true') this.setUpStickyBar();
  }

  disconnectedCallback() {
    this.stickyObserver?.disconnect();
    this.stickyBar?.remove();
  }

  /** @param {number} cents */
  money(cents) {
    // Match Liquid's money_without_trailing_zeros: "Rs.3,500" rather than "Rs.3,500.00".
    return formatMoney(cents, this.moneyFormat, this.currency).replace(/[.,]00(?!\d)/, '');
  }

  /**
   * Returns the cart lines and the prices to display.
   * @returns {{ lines: Array<{ id: string, quantity: number }>, total: number, original: number, bundleCount: number }}
   */
  getSelection() {
    const lines = [];
    let total = 0;
    let original = 0;

    const main = /** @type {HTMLElement | null} */ (this.querySelector('[data-qo-main]'));
    if (main) {
      const checkedQty = /** @type {HTMLInputElement | null} */ (this.querySelector('[name="qo-qty"]:checked'));
      const quantity = checkedQty?.value === 'bulk' ? this.bulkQuantity() : Math.max(1, parseInt(checkedQty?.value || '1', 10));
      const price = Number(main.dataset.price);
      const dealQty = Number(main.dataset.dealQty || 0);
      const dealOffEach = Number(main.dataset.dealOffEach || 0);
      let dealOff = 0;
      if (dealQty && quantity >= dealQty) {
        // Per-copy discounts scale with quantity; otherwise the deal is a fixed amount off the order.
        dealOff = dealOffEach ? dealOffEach * quantity : Number(main.dataset.dealOff || 0);
      }

      lines.push({ id: main.dataset.variantId, quantity });
      original += price * quantity;
      total += price * quantity - dealOff;
    }

    this.querySelectorAll('[data-qo-addon]:checked').forEach((input) => {
      lines.push({ id: input.value, quantity: 1 });
      original += Number(input.dataset.price);
      total += Number(input.dataset.salePrice);
    });

    const bundleInputs = /** @type {NodeListOf<HTMLInputElement>} */ (this.querySelectorAll('[data-qo-bundle]:checked'));
    const bundleMin = Number(this.dataset.bundleMin || 0);
    const bundlePct = Number(this.dataset.bundlePct || 0);
    const bundleQualifies = bundleInputs.length >= bundleMin && bundlePct > 0;
    bundleInputs.forEach((input) => {
      const price = Number(input.dataset.price);
      lines.push({ id: input.value, quantity: 1 });
      original += price;
      // Shopify applies a percentage discount per line, rounded to the cent.
      total += bundleQualifies ? price - Math.round((price * bundlePct) / 100) : price;
    });

    return { lines, total, original, bundleCount: bundleInputs.length };
  }

  updateTotals() {
    const { lines, total, original, bundleCount } = this.getSelection();
    const savings = original - total;

    const totalEl = this.querySelector('[data-qo-total]');
    const compareEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-qo-compare]'));
    const savingsEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-qo-savings]'));
    const labelEl = this.querySelector('[data-qo-button-label]');
    const submit = /** @type {HTMLButtonElement | null} */ (this.form.querySelector('button[type="submit"]'));

    if (totalEl) totalEl.textContent = this.money(total);
    if (compareEl) {
      compareEl.hidden = savings <= 0;
      compareEl.textContent = this.money(original);
    }
    if (savingsEl) {
      savingsEl.hidden = savings <= 0;
      savingsEl.textContent = `Aap ki bachat: ${this.money(savings)}`;
    }
    if (labelEl && submit) labelEl.textContent = `${submit.dataset.label} · ${this.money(total)}`;
    if (submit) submit.disabled = lines.length === 0;

    const stickyTotal = (this.stickyBar || this).querySelector('[data-qo-sticky-total]');
    if (stickyTotal) stickyTotal.textContent = this.money(total);

    const hint = /** @type {HTMLElement | null} */ (this.querySelector('[data-qo-bundle-hint]'));
    if (hint) hint.hidden = bundleCount >= Number(this.dataset.bundleMin || 0);

    const bulkTotal = this.querySelector('[data-qo-bulk-total]');
    const main = /** @type {HTMLElement | null} */ (this.querySelector('[data-qo-main]'));
    if (bulkTotal && main) {
      const quantity = this.bulkQuantity();
      const each = Number(main.dataset.price) - Number(main.dataset.dealOffEach || 0);
      bulkTotal.textContent = `${quantity} × ${this.money(each)} = ${this.money(each * quantity)}`;
      const bulkSave = this.querySelector('[data-qo-bulk-save]');
      if (bulkSave) bulkSave.textContent = `${this.money(Number(main.dataset.dealOffEach || 0) * quantity)} bachat`;
    }
  }

  /** Bulk option quantity, kept within the input's min/max. */
  bulkQuantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-qo-bulk-qty]'));
    if (!input) return 1;
    const min = Number(input.min) || 1;
    const max = Number(input.max) || min;
    const value = parseInt(input.value, 10);
    return Math.min(max, Math.max(min, Number.isNaN(value) ? min : value));
  }

  setUpBulkStepper() {
    const input = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-qo-bulk-qty]'));
    if (!input) return;
    const radio = /** @type {HTMLInputElement | null} */ (this.querySelector('[name="qo-qty"][value="bulk"]'));

    const select = () => {
      if (radio) radio.checked = true;
      this.updateTotals();
    };

    this.querySelectorAll('[data-qo-bulk-step]').forEach((button) => {
      button.addEventListener('click', (event) => {
        // The buttons sit inside the option's <label>; stop the click from also toggling it.
        event.preventDefault();
        input.value = String(this.bulkQuantity() + Number(/** @type {HTMLElement} */ (button).dataset.qoBulkStep));
        input.value = String(this.bulkQuantity());
        select();
      });
    });
    input.addEventListener('input', select);
    input.addEventListener('change', () => {
      input.value = String(this.bulkQuantity());
      select();
    });
  }

  restoreDetails() {
    const details = readDetails();
    if (!details) return;

    let filled = false;
    for (const field of FIELDS) {
      const input = /** @type {HTMLInputElement | null} */ (this.form.elements.namedItem(field));
      if (input && !input.value && details[field]) {
        input.value = details[field];
        filled = true;
      }
    }
    const notice = /** @type {HTMLElement | null} */ (this.querySelector('[data-qo-remembered]'));
    if (notice) notice.hidden = !filled;
  }

  forgetDetails() {
    writeDetails(null);
    for (const field of FIELDS) {
      const input = /** @type {HTMLInputElement | null} */ (this.form.elements.namedItem(field));
      if (input) input.value = '';
    }
    const notice = /** @type {HTMLElement | null} */ (this.querySelector('[data-qo-remembered]'));
    if (notice) notice.hidden = true;
  }

  /** @param {string} message */
  showError(message) {
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-qo-error]'));
    if (!error) return;
    error.textContent = message;
    error.hidden = !message;
  }

  /** @param {SubmitEvent} event */
  handleSubmit(event) {
    event.preventDefault();

    const data = new FormData(this.form);
    const values = Object.fromEntries(FIELDS.map((field) => [field, String(data.get(field) || '').trim()]));

    const missing = FIELDS.find((field) => !values[field]);
    if (missing) {
      this.showError('Meharbani kar ke naam, phone, address aur city sab likhein.');
      /** @type {HTMLInputElement | null} */ (this.form.elements.namedItem(missing))?.focus();
      return;
    }

    values.phone = normalisePhone(values.phone);
    if (!/^\+?\d{10,15}$/.test(values.phone)) {
      this.showError('Phone number durust likhein, jaise 03001234567.');
      /** @type {HTMLInputElement | null} */ (this.form.elements.namedItem('phone'))?.focus();
      return;
    }

    const { lines } = this.getSelection();
    if (lines.length === 0) {
      this.showError('Kam az kam ek kitab select karein.');
      return;
    }
    this.showError('');

    if (this.remember) writeDetails(values);

    const nameParts = values.name.split(/\s+/);
    const firstName = nameParts.shift() || values.name;
    const lastName = nameParts.join(' ') || firstName;

    const params = new URLSearchParams();
    params.set('checkout[shipping_address][first_name]', firstName);
    params.set('checkout[shipping_address][last_name]', lastName);
    params.set('checkout[shipping_address][address1]', values.address);
    params.set('checkout[shipping_address][city]', values.city);
    params.set('checkout[shipping_address][phone]', values.phone);
    params.set('checkout[shipping_address][country]', 'Pakistan');
    // Discounted add-on prices rely on a discount code that is only sent when one of them is ticked;
    // full-price extras (e.g. the tasbeeh counter) must not trigger it, or checkout shows "code not valid".
    if (this.dataset.addonCode && this.querySelector('[data-qo-addon][data-qo-discounted]:checked')) {
      params.set('discount', this.dataset.addonCode);
    }

    const submitButton = /** @type {HTMLButtonElement | null} */ (this.form.querySelector('button[type="submit"]'));
    if (submitButton) {
      submitButton.disabled = true;
      const label = submitButton.querySelector('[data-qo-button-label]');
      if (label && submitButton.dataset.loadingText) label.textContent = submitButton.dataset.loadingText;
    }

    const items = lines.map((line) => `${line.id}:${line.quantity}`).join(',');
    const root = window.Shopify?.routes?.root || '/';
    window.location.href = `${root}cart/${items}?${params.toString()}`;
  }

  setUpStickyBar() {
    const bar = /** @type {HTMLElement | null} */ (this.querySelector('[data-qo-sticky]'));
    if (!bar || !('IntersectionObserver' in window)) return;

    // Keep the bar outside the form's layout so `position: fixed` isn't affected by transformed ancestors.
    document.body.appendChild(bar);
    this.stickyBar = bar;

    bar.querySelector('[data-qo-sticky-button]')?.addEventListener('click', () => {
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const firstEmpty = FIELDS.map((field) => /** @type {HTMLInputElement | null} */ (this.form.elements.namedItem(field)))
        .find((input) => input && !input.value);
      (firstEmpty || this.form.querySelector('button[type="submit"]'))?.focus({ preventScroll: true });
    });

    this.stickyObserver = new IntersectionObserver(([entry]) => {
      bar.hidden = Boolean(entry?.isIntersecting);
    });
    this.stickyObserver.observe(this.form);
  }
}

if (!customElements.get('quick-order-form')) {
  customElements.define('quick-order-form', QuickOrderForm);
}
